'use client';

import clsx from 'clsx';
import { SearchIcon } from 'lucide-react';
import Link from 'next/link';
import type { UseFormRegisterReturn } from 'react-hook-form';
import { Spinner } from '../Spinner';
import styles from './InputSearch.module.scss';

type InputSearchAllowedChildren =
  | React.ReactElement<React.ComponentProps<typeof InputSearchSuggestion>>
  | boolean;

interface InputSearchActionProps {
  label: React.ReactNode;
  title: string;
  disabled?: boolean;
  onClick: () => void;
}

export type InputSearchTone = 'default' | 'surface';

interface InputSearchProps extends React.HTMLProps<HTMLInputElement> {
  id: string;
  label: string;
  children?:
    | InputSearchAllowedChildren
    | InputSearchAllowedChildren[]
    | (InputSearchAllowedChildren | InputSearchAllowedChildren[])[];
  register?: UseFormRegisterReturn;
  suggestionExpanded?: boolean; // Set to true when the suggestion list is expanded
  suggestionListId?: string; // ID of the suggestion list
  suggestionActiveOptionId?: string; // ID of the active suggestion option
  loading?: boolean;
  testId?: string;
  button?: InputSearchActionProps;
  className?: string;
  tone?: InputSearchTone;
  limitWidth?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export const InputSearch: React.FC<InputSearchProps> = ({
  id,
  label,
  children,
  register,
  suggestionExpanded,
  suggestionListId,
  suggestionActiveOptionId,
  loading,
  testId = id,
  button,
  className,
  tone = 'default',
  onChange,
  onKeyDown,
  ref,
  limitWidth = false,
  ...props
}) => {
  // Only apply combobox ARIA attributes when suggestions are available
  const hasComboboxBehavior = suggestionListId && suggestionExpanded !== undefined;

  return (
    <div
      role={hasComboboxBehavior ? 'combobox' : undefined}
      aria-expanded={hasComboboxBehavior ? suggestionExpanded : undefined}
      aria-controls={hasComboboxBehavior ? suggestionListId : undefined}
      aria-haspopup={hasComboboxBehavior ? 'listbox' : undefined}
      className={clsx(styles.combobox, className, {
        [styles.comboboxBgSurface]: tone === 'surface',
        [styles.comboboxLimitWidth]: limitWidth,
      })}
      ref={ref}
    >
      <div className={styles.inputWrapper}>
        <label htmlFor={id} className={styles.iconLabel}>
          <SearchIcon aria-hidden="true" />
          <span className="sr-only">{label}</span>
        </label>
        <input
          type="search"
          aria-autocomplete={hasComboboxBehavior ? 'list' : undefined}
          aria-activedescendant={
            hasComboboxBehavior && suggestionActiveOptionId ? suggestionActiveOptionId : undefined
          }
          id={id}
          aria-label={label}
          className={styles.input}
          onChange={onChange}
          onKeyDown={onKeyDown}
          data-testid={testId}
          enterKeyHint="search"
          {...props}
          {...register}
        />
        {loading && (
          <span className={styles.loader}>
            <Spinner size="text" />
          </span>
        )}
        {button && (
          <button
            type="button"
            onClick={button.onClick}
            aria-label={button.title}
            disabled={button.disabled}
            className={styles.button}
          >
            {button.label}
          </button>
        )}
      </div>
      {children}
    </div>
  );
};

// Suggestion List

type SuggestionListAllowedChildren =
  | React.ReactElement<typeof InputSearchSuggestionGroup>
  | boolean;

interface InputSearchSuggestionProps {
  children?: SuggestionListAllowedChildren | SuggestionListAllowedChildren[];
  id: string;
  testId?: string;
}

export const InputSearchSuggestion: React.FC<InputSearchSuggestionProps> = ({
  children,
  id,
  testId = id,
}) => (
  <ul id={id} className={styles.list} data-testid={testId}>
    {children}
  </ul>
);

// Suggestion Group

export type SuggestionGroupAllowedChildren = React.ReactElement<
  typeof InputSearchSuggestionOption
> | null;

export interface InputSearchSuggestionGroupProps {
  id: string;
  label: string;
  children: SuggestionGroupAllowedChildren | SuggestionGroupAllowedChildren[];
}

export const InputSearchSuggestionGroup: React.FC<InputSearchSuggestionGroupProps> = ({
  id,
  label,
  children,
}) => (
  <li aria-labelledby={`${id}-label`} className={styles.group}>
    <div id={`${id}-label`} className={styles.groupLabel}>
      {label}
    </div>
    {children}
  </li>
);

// Suggestion Option

interface InputSearchSuggestionOptionProps extends React.HTMLProps<HTMLAnchorElement> {
  id: string;
  href: string;
  title?: string;
  count?: number;
  isActive?: boolean;
  children: React.ReactNode;
  testId?: string;
  onMouseEnter?: () => void;
}

export const InputSearchSuggestionOption: React.FC<InputSearchSuggestionOptionProps> = ({
  id,
  href,
  title,
  count,
  isActive,
  children,
  testId = id,
  onMouseEnter,
  ...props
}) => (
  <Link
    href={href}
    title={title}
    id={id}
    className={clsx(styles.option, { [styles.optionActive]: isActive })}
    data-testid={testId}
    tabIndex={-1}
    onMouseEnter={onMouseEnter}
    {...props}
  >
    <span className={styles.optionLabel}>{children}</span>
    {count && (
      <>
        <span className="sr-only">({count} items)</span>
        <span className={styles.optionCount} aria-hidden="true">
          {count}
        </span>
      </>
    )}
  </Link>
);
